// Đăng ký Web Push cho user đang login.
// Flow:
//   1. Check Notification + PushManager + SW support.
//   2. Register /sw.js. Fetch VAPID public key.
//   3. Request permission (lazy — sau click đầu tiên, để tránh Chrome block).
//   4. Subscribe PushManager → POST subscription lên backend.
//   5. Listen message NAVIGATE từ SW → navigate()
//
// Tắt tab vẫn nhận noti (OS-level). Yêu cầu: admin mở HTTPS (connect.cnvwork.com OK).
//
// CNV Work desktop (Electron): SKIP toàn bộ. Chromium push service KHÔNG
// available trong Electron → pushManager.subscribe() fail "push service not
// available". Desktop dùng path khác: socket events → cnvDesktop.showNotification
// (bridge ra native macOS/Win/Linux noti). Xem ChatPushNotifications +
// TeamChatPushNotifications.

import { useEffect } from 'react';
import { useAuth } from '@host/auth';
import { useNavigate } from 'react-router-dom';
import api from '@host/axios';
import { isDesktopApp } from '../../lib/cnvDesktop';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const base = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function deviceLabel() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Win' : /Android/.test(ua) ? 'Android' : /iPhone|iPad|iPod/.test(ua) ? 'iOS' : 'Device';
  return `${browser} ${os}`.substring(0, 100);
}

let _registering = false;
let _subscribedEndpoint = null;    // Endpoint đã POST /subscribe — skip duplicate trong session

async function registerAndSubscribe() {
  if (_registering) return;
  _registering = true;
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      console.warn('[webpush] Browser không hỗ trợ Push API');
      return;
    }
    if (Notification.permission === 'denied') {
      console.warn('[webpush] Permission denied — user cần bật lại trong browser settings');
      return;
    }

    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    // Xin permission NẾU chưa có
    if (Notification.permission !== 'granted') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') { console.warn('[webpush] permission:', p); return; }
    }

    // Đã subscribe chưa?
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Fetch VAPID public key
      const r = await api.get('/notifications/push/vapid-key');
      const vapid = r.data?.data?.publicKey;
      if (!vapid) { console.error('[webpush] No VAPID key'); return; }
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
      console.log('[webpush] new subscription created');
    }

    const json = sub.toJSON();
    // Idempotency: skip POST nếu cùng endpoint đã subscribed trong session.
    // BE đã upsert theo endpoint (unique key) — POST duplicate là no-op BE-side
    // nhưng FE-side gây spam log + tăng quota request. Reset khi user logout
    // (Effect cleanup KHÔNG cần — module-level flag sống ngắn theo tab lifetime).
    if (_subscribedEndpoint === json.endpoint) {
      return;
    }
    await api.post('/notifications/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
      deviceLabel: deviceLabel(),
    });
    _subscribedEndpoint = json.endpoint;
    console.log('[webpush] subscription saved to backend');
  } catch (e) {
    console.error('[webpush] register error:', e?.message || e);
  } finally {
    _registering = false;
  }
}

export default function WebPushRegister() {
  const { user } = useAuth();
  const userId = user?.id;
  const navigate = useNavigate();

  // Register sau khi có user login. Dùng lazy trigger click/keydown để permission request có gesture.
  // QUAN TRỌNG (2026-05-27 fix burst 429): dep `[userId]` (string primitive) thay vì
  // `[user]` (object identity). User object reference đổi khi axios refresh token
  // gọi setSession → useEffect [user] re-run → POST /notifications/push/subscribe loop.
  // userId stable cho cùng 1 user → effect chỉ re-run khi user THẬT SỰ đổi tài khoản.
  useEffect(() => {
    if (!userId) return;
    if (isDesktopApp) {
      console.log('[webpush] skip — running in CNV Work desktop (native noti via cnvDesktop bridge)');
      return;
    }
    if (!('serviceWorker' in navigator)) return;

    // Nếu đã granted → subscribe luôn
    if ('Notification' in window && Notification.permission === 'granted') {
      registerAndSubscribe();
      return;
    }
    // Chưa granted → đợi gesture đầu tiên
    const once = () => {
      window.removeEventListener('click', once);
      window.removeEventListener('keydown', once);
      registerAndSubscribe();
    };
    window.addEventListener('click', once, { once: true });
    window.addEventListener('keydown', once, { once: true });
    return () => {
      window.removeEventListener('click', once);
      window.removeEventListener('keydown', once);
    };
  }, [userId]);

  // Listen NAVIGATE message từ SW khi user click notification
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        try {
          const url = new URL(event.data.url, window.location.origin);
          navigate(url.pathname + url.search);
        } catch {
          navigate(event.data.url);
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  return null;
}
